import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LayoutGrid, ChevronDown, Pencil, Check, Pin, Plus } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useNavConfig } from '../../context/NavConfigContext';
import { NAV_CATEGORIES, NAV_FUNCTIONS } from '../../lib/navFunctions';
import api from '../../lib/api';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../ui/dropdown-menu';

// Lanciatore rapido: apre una tendina con TUTTE le funzioni visibili, raggruppate
// per categoria. In "modalità modifica" (solo admin) permette di aggiungere/rimuovere
// le funzioni dalla sidebar sinistra (pin/unpin).
export const FunctionsLauncher = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPerm, user } = useAuth();
  const { isPinned, pinFunction, unpinFunction } = useNavConfig();
  const [sections, setSections] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    api.get('/sections').then(({ data }) => {
      const map = {};
      (data.sections || []).forEach((s) => { map[s.key] = s; });
      setSections(map);
    }).catch(() => setSections({}));
  }, []);

  const isVisible = (item) => {
    if (!hasPerm(item.perm)) return false;
    if (!sections) return true;
    const sec = sections[item.perm];
    if (!sec) return true;
    if (!sec.enabled) return false;
    return (sec.roles || []).includes(user?.role);
  };

  const groups = NAV_CATEGORIES.map((cat) => ({
    ...cat,
    items: NAV_FUNCTIONS.filter((f) => f.cat === cat.key && isVisible(f)),
  })).filter((g) => g.items.length > 0);

  const togglePin = (key) => {
    if (isPinned(key)) unpinFunction(key);
    else pinFunction(key);
  };

  return (
    <DropdownMenu onOpenChange={(o) => { if (!o) setEditMode(false); }}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="h-10 sm:h-11 gap-2 rounded-xl shadow-sm-token"
          data-testid="topbar-functions-launcher-trigger"
          aria-haspopup="menu"
        >
          <LayoutGrid className="h-5 w-5" />
          <span className="hidden sm:inline">{t('nav.funzioni')}</span>
          <ChevronDown className="h-4 w-4 opacity-70" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="center"
        sideOffset={8}
        collisionPadding={12}
        className="w-[min(720px,calc(100vw-24px))] p-0"
        data-testid="topbar-functions-launcher-panel"
      >
        {/* Header pannello: titolo + pulsante modifica (solo admin) — resta fisso in alto */}
        <div className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-border bg-popover px-3 py-2">
          <span className="text-sm font-semibold" data-testid="functions-launcher-header-title">
            {editMode ? t('launcher.editTitle', { defaultValue: 'Aggiungi funzioni alla sidebar' }) : t('nav.funzioni')}
          </span>
          {isAdmin && (
            <Button
              variant={editMode ? 'default' : 'ghost'}
              size="icon"
              className="h-8 w-8 rounded-lg"
              onClick={() => setEditMode((e) => !e)}
              data-testid="functions-launcher-edit-toggle"
              title={editMode ? t('launcher.editDone', { defaultValue: 'Fine' }) : t('launcher.editStart', { defaultValue: 'Modifica sidebar' })}
              aria-pressed={editMode}
            >
              {editMode ? <Check className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
            </Button>
          )}
        </div>
        <div className="p-2 sm:p-3 space-y-3">
            {editMode && (
              <p className="px-2 text-xs text-muted-foreground" data-testid="functions-launcher-edit-hint">
                {t('launcher.editHint', { defaultValue: 'Tocca una funzione per aggiungerla o rimuoverla dal menu laterale.' })}
              </p>
            )}
            {groups.map((cat) => (
              <div key={cat.key} data-testid={`functions-launcher-category-${cat.key}`}>
                <div className="px-2 pt-1 pb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground" data-testid="functions-launcher-category-title">
                  {t(cat.sKey)}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                  {cat.items.map((fn) => {
                    const Icon = fn.icon;
                    const pinned = isPinned(fn.key);
                    return (
                      <DropdownMenuItem
                        key={fn.key}
                        data-testid={`functions-launcher-item-${fn.key}`}
                        onSelect={(e) => {
                          if (editMode) { e.preventDefault(); togglePin(fn.key); }
                          else navigate(fn.path);
                        }}
                        className={cn(
                          'group flex items-center gap-3 rounded-xl px-3 py-3 cursor-pointer focus:bg-muted active:scale-[0.98] transition-transform duration-100',
                          editMode && pinned && 'border border-primary/40 bg-primary/5',
                          editMode && !pinned && 'border border-dashed border-border'
                        )}
                      >
                        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary shrink-0" data-testid="functions-launcher-item-icon">
                          <Icon className="h-5 w-5" />
                        </span>
                        <span className="min-w-0 flex-1 text-sm font-semibold leading-tight truncate" data-testid="functions-launcher-item-title">
                          {t(fn.tKey)}
                        </span>
                        {editMode && (
                          <span
                            className={cn('flex h-6 w-6 items-center justify-center rounded-full shrink-0',
                              pinned ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground')}
                            data-testid={`functions-launcher-pin-${fn.key}`}
                          >
                            {pinned ? <Pin className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                          </span>
                        )}
                      </DropdownMenuItem>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
